import { Fragment, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'

/**
 * The three inline marks a help article may use, drawn as React rather than
 * parsed as Markdown: `**bold**` for a control's name, `` `key` `` for a key,
 * and `[text](help:id)` or `[text](/path)` for a link. Anything else is text.
 */

export type GuideLink = { kind: 'article'; id: string } | { kind: 'route'; to: string }

export type LinkRenderer = (link: GuideLink, children: ReactNode, key: string) => ReactNode

const MARK = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex min-w-[1.6em] items-center justify-center rounded-sm bg-white px-1.5 py-px font-sans text-[0.82em] leading-[1.45] font-semibold text-ink-800 shadow-[inset_0_-1.5px_0_var(--color-ink-300)] ring-1 ring-ink-300',
        className,
      )}
    >
      {children}
    </kbd>
  )
}

export function Markup({ text, renderLink }: { text: string; renderLink: LinkRenderer }) {
  const parts = text.split(MARK)
  return (
    <>
      {parts.map((part, index) => {
        const key = String(index)
        if (!part) return null
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={key} className="font-semibold text-ink-900">
              {part.slice(2, -2)}
            </strong>
          )
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return <Kbd key={key}>{part.slice(1, -1)}</Kbd>
        }
        const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part)
        if (link) {
          const [, label = '', target = ''] = link
          return renderLink(
            target.startsWith('help:')
              ? { kind: 'article', id: target.slice('help:'.length) }
              : { kind: 'route', to: target },
            label,
            key,
          )
        }
        return <Fragment key={key}>{part}</Fragment>
      })}
    </>
  )
}

/** The class every in-text link shares, so a guide's links read as one family. */
export const guideLinkClass =
  'font-medium text-brand-700 underline decoration-brand-700/30 underline-offset-2 hover:decoration-brand-700'
